/**
 * The reads and writes for §5.2 — W12, W14…W21 and W24 — that `api/queries.ts`
 * does not already provide.
 *
 * WHY ANYTHING IS HERE AT ALL. The shared factory (`qk`) covers exactly two of
 * the thirteen team endpoints: **W13** `/teams/lookup` (`qk.teams.lookup`, the
 * filter dropdowns) and **W22** `/teams/{id}/roster` (`qk.teams.roster`). Both
 * are reference reads that several screens want; the rest belong to this screen
 * and are declared here rather than swelling the shared factory with keys only
 * one route uses. The same reasoning `admin/-shared/admin-api.ts` gives.
 *
 * The one request body below is transcribed from the API's own DTO
 * (`workforce/dto/team.dto.ts`), NOT invented: `packages/shared` declares
 * `CreateTeamRequest`, `UpdateTeamRequest`, `CreateMembershipRequest`,
 * `UpdateMembershipRequest`, `CreateTeamLeaderRequest` and
 * `EndTeamLeaderRequest`, but never declared a body for W21. A body that
 * arrives in shared later should replace its local twin here rather than sit
 * alongside it.
 *
 * ── PERMISSIONS ARE NOT UNIFORM ACROSS THIS FILE ────────────────────────────
 * `team.read` reads · `team.write` writes (HR, ADMIN) — EXCEPT the leader
 * endpoints (ADR-0013). **W24** `GET /teams/{id}/leaders` is still
 * `team.read` — a leader window is a fact anyone who can see the team may
 * read — but **W25** `POST /teams/{id}/leaders`, **W26**
 * `POST /teams/{id}/leaders/{leaderId}/end`, **W39**
 * `DELETE /teams/{id}/leaders/{leaderId}` and **W40** `PATCH` on the same path
 * are **`roster.confirm`**
 * (SUPERVISOR, ADMIN). Transcribed from `teams.controller.ts`, not inferred:
 * an HR user may create the team and assign its crew and still be refused the
 * leader write, and a Supervisor is the reverse. Every caller gates on the
 * permission named beside the endpoint below.
 */
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type {
  CreateMembershipRequest,
  CreateTeamLeaderRequest,
  CreateTeamRequest,
  EndTeamLeaderRequest,
  IsoDate,
  Paginated,
  TeamDto,
  TeamLeaderDto,
  TeamMembershipDto,
  UpdateMembershipRequest,
  UpdateTeamLeaderRequest,
  UpdateTeamRequest,
} from '@cropmate/shared';
import { api, type QueryParams } from '@/api/client';

const MIN = 60_000;

/* ═══════════════════════════ 1. Bodies not in shared ═════════════════════ */

/**
 * W21 `POST /teams/{id}/memberships/{workerId}/end`.
 * `validTo` is **EXCLUSIVE** — "ended on the 31st" is `validTo` the 1st. The
 * drawer collects the inclusive last day and converts; see
 * `-membership-window.ts`.
 */
export interface EndMembershipRequest {
  validTo: IsoDate;
}

/* ═══════════════════════════ 2. Reads ════════════════════════════════════ */

export const teamsQk = {
  /**
   * W12 `GET /teams` — teams with member counts and the default leader, both
   * resolved AS AT `asOf`. A distinct key from `qk.teams.lookup`: that one
   * returns `TeamRefDto[]` and this returns `Paginated<TeamDto>`, so sharing a
   * cache slot would blow up at `.items`.
   */
  list: (p: QueryParams = {}) =>
    queryOptions({
      queryKey: ['teams', 'list', p] as const,
      queryFn: () => api.get<Paginated<TeamDto>>('/teams', p),
      staleTime: MIN,
    }),

  /**
   * W18 `GET /teams/{id}/memberships`.
   *
   * `includeHistory` changes what the endpoint MEANS, not just how much it
   * returns: without it the server filters to the windows covering `asOf`, and
   * `asOf` is then ignored. It is part of the key for that reason.
   */
  memberships: (teamId: string, p: QueryParams = {}) =>
    queryOptions({
      queryKey: ['teams', 'memberships', teamId, p] as const,
      queryFn: () => api.get<Paginated<TeamMembershipDto>>(`/teams/${teamId}/memberships`, p),
      staleTime: 30_000,
    }),

  /**
   * W24 `GET /teams/{id}/leaders` — `team.read`. Every window on the team,
   * newest first (ADR-0013), and unlike W18's memberships there is no
   * `includeHistory` toggle: the Leader section always wants the full run so
   * it can find the one window covering `today` itself, dim the rest, and
   * hand `findOverlap` the same history a courtesy pre-check needs.
   */
  leaders: (teamId: string) =>
    queryOptions({
      queryKey: ['teams', 'leaders', teamId] as const,
      queryFn: () => api.get<TeamLeaderDto[]>(`/teams/${teamId}/leaders`),
      staleTime: 30_000,
    }),
};

/* ═══════════════════════════ 3. Writes ═══════════════════════════════════ */

export const teamsApi = {
  /** W14 — `team.write`. 409 `E_CONFLICT_DUPLICATE` on a code already taken. */
  createTeam: (body: CreateTeamRequest) => api.post<TeamDto>('/teams', body),

  /** W16 — `team.write`. PATCH: absent leaves unchanged (§1.2). */
  updateTeam: (id: string, body: UpdateTeamRequest) => api.patch<TeamDto>(`/teams/${id}`, body),

  /**
   * W17 — `team.write`. Soft (FR-1.5), and REFUSED with `409 E_FK_VIOLATION`
   * while the team still has a member live today; `meta.liveMemberships` counts
   * them. Emptying the team first is deliberate: silently orphaning a roster is
   * how a day sheet loses its crew.
   */
  deleteTeam: (id: string) => api.del(`/teams/${id}`),

  /**
   * W19 — `team.write`. **409 `E_TEAM_MEMBERSHIP_OVERLAP`** when the worker is
   * already on a team for any covered date (FR-2.6). That refusal comes from
   * `team_membership_no_overlap_excl`, whose equality key is `worker_id` ALONE —
   * so it fires across teams, not just within one, and it is the whole point of
   * the rule: one team per worker per date.
   */
  createMembership: (teamId: string, body: CreateMembershipRequest) =>
    api.post<TeamMembershipDto>(`/teams/${teamId}/memberships`, body),

  /**
   * W20 — `team.write`. `(teamId, workerId)` is not a key: a worker can leave a
   * team and come back, so the API amends the row covering today, else the most
   * recent one.
   */
  updateMembership: (teamId: string, workerId: string, body: UpdateMembershipRequest) =>
    api.patch<TeamMembershipDto>(`/teams/${teamId}/memberships/${workerId}`, body),

  /** W21 — `team.write`. Command path is `/end`, never `:end` (§1.12). */
  endMembership: (teamId: string, workerId: string, body: EndMembershipRequest) =>
    api.post<TeamMembershipDto>(`/teams/${teamId}/memberships/${workerId}/end`, body),

  /**
   * W41 — `team.write`. Soft-deletes a membership added by mistake, which W21
   * cannot remove (its earliest last day is the first day). `validFrom` pins the
   * exact window. `409 E_CONFIG_LOCKED_PERIOD` across a LOCKED pay period.
   */
  deleteMembership: (teamId: string, workerId: string, validFrom: IsoDate) =>
    api.del(`/teams/${teamId}/memberships/${workerId}`, { query: { validFrom } }),

  /**
   * W25 — **`roster.confirm`**, not `team.write`. See the file header.
   * `409 E_TEAM_LEADER_OVERLAP` when the team already has a live window
   * (`team_leader_no_overlap_excl`); `422 E_INVALID_DATE_RANGE` for an empty
   * range.
   */
  createLeader: (teamId: string, body: CreateTeamLeaderRequest) =>
    api.post<TeamLeaderDto>(`/teams/${teamId}/leaders`, body),

  /**
   * W26 — **`roster.confirm`**. Command path is `/end`, never `:end`
   * (§1.12), same as W21.
   */
  endLeader: (teamId: string, leaderId: string, body: EndTeamLeaderRequest) =>
    api.post<TeamLeaderDto>(`/teams/${teamId}/leaders/${leaderId}/end`, body),

  /**
   * W40 — **`roster.confirm`**. Dates and note only; the worker is fixed (a
   * different person is Delete + Assign). `409 E_TEAM_LEADER_OVERLAP`,
   * `409 E_CONFIG_LOCKED_PERIOD` when the dates added or removed touch a LOCKED
   * period, `422 E_INVALID_DATE_RANGE`.
   */
  updateLeader: (teamId: string, leaderId: string, body: UpdateTeamLeaderRequest) =>
    api.patch<TeamLeaderDto>(`/teams/${teamId}/leaders/${leaderId}`, body),

  /**
   * W39 — **`roster.confirm`**. Removes a window assigned by mistake, which W26
   * cannot: End's earliest last day is the start day. `204`, or
   * `409 E_CONFIG_LOCKED_PERIOD` when the window intersects a LOCKED pay
   * period (BR-C1) — `meta.periodCode` names it.
   */
  deleteLeader: (teamId: string, leaderId: string) =>
    api.del(`/teams/${teamId}/leaders/${leaderId}`),
};

/* ═══════════════════════════ 4. Invalidation ═════════════════════════════ */

/**
 * A team, membership or leader write moves more than this screen.
 *
 * `['workers']` is NOT optional here: `WorkerProfileDto.currentTeam` and
 * `.teamHistory` are both membership-derived, and the register's `Team` column
 * resolves the membership covering `asOf`. Without it, assigning a worker to a
 * team leaves the profile panel that triggered the write still showing "no
 * team" until its own staleTime expires. It also covers `WorkerProfileDto.leads`
 * (ADR-0013) for the same reason: a worker just made a leader would otherwise
 * show a stale empty list on their own profile.
 *
 * `['teams']` is a broad key on purpose: it invalidates `teamsQk.list` (the
 * `TeamDto.leader` column), `teamsQk.memberships` AND `teamsQk.leaders`
 * together, since a single write — assigning or ending a leader — changes what
 * all three would return.
 *
 * `['job-day']` goes too: W22's roster — the crew a day sheet pre-selects — is
 * built from the memberships covering the work date (`teams.service.ts`), so a
 * membership written today changes who appears on today's sheet. A leader
 * write belongs here for the same reason: the day sheet's leader box (who is
 * credited the overriding on that date) reads the `team_leader` window, not a
 * membership.
 */
export async function invalidateTeams(qc: QueryClient): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['teams'] }),
    qc.invalidateQueries({ queryKey: ['workers'] }),
    qc.invalidateQueries({ queryKey: ['job-day'] }),
  ]);
}
